import React, { useState, useEffect, Fragment } from 'react'
import { useWithAuth } from '../../containers/App'
import { useForm } from 'react-hook-form'
import api from '../../utils/api'
import styled from 'styled-components'
import { useMutation } from 'react-query'
import { useNotifications } from '../../containers/Notifications'
import Modal from '../Modal'
import Button from '../../components/Button'
import colors from '../../styles/colors'

import { PlaceHolder } from '../../utils/withLoading'
import { ActiveQuestion, questionType } from './utils'

type Props = {
  preview?: boolean
}

function Survey({ preview }: Props) {
  const surveyId = location.pathname.substring(
    location.pathname.lastIndexOf('/') + 1
  )
  const [survey, setSurvey] = useState({
    title: '',
    description: '',
  })
  const [approveSendModal, setApproveSendModal] = useState<boolean>(false)
  const [doneQuestions, setDoneQuestions] = useState<Array<ActiveQuestion>>([])
  const [sentImages, setSentImages] = useState({ files: [], filesId: [] })
  const endpoint = preview ? '/form/rest/survey/' : '/form/rest/survey/request/'
  const surveyImagesEndpoint = '/file/rest/image/'
  const surveyCompletionEndpoint = '/form/rest/survey/request/'
  const withAuth = useWithAuth()
  const form = useForm({})
  const watchEveryField = form.watch()
  const { notify } = useNotifications()
  const isFilled = Object.entries(watchEveryField)
    .map((value) => value[1] !== '')
    .includes(false)

  const [mutate] = useMutation<any, any>((values) =>
    api(`${surveyCompletionEndpoint}${surveyId}/response`, {
      method: 'POST',
      payload: { answers: values },
    })
      .then((res) => {
        if ('fieldErrors' in res) {
          // @todo notifications
        } else {
          notify(
            'Dziękujemy za wypełnienie ankiety!\nTwoje odpowiedzi zostały przesłane.',
            'success'
          )
        }
      })
      .catch((e) => {
        e.status === 403
          ? notify(' brak wymaganych uprawnień', 'error')
          : notify('Nie udało się wysłać ankietę', 'error')
      })
  )

  const fetchSurvey = async () => {
    const result = preview
      ? await withAuth(`${endpoint}${surveyId}`, {
          method: 'GET',
        })
      : await api(`${endpoint}${surveyId}`, {
          method: 'GET',
        })
    return result
  }
  const fetchSurveyAnswerImage = async (id) => {
    const result = await api(`${surveyImagesEndpoint}${id}`, {
      method: 'GET',
      responseType: 'blob',
    })
    //await setImages([...images,{ file: URL.createObjectURL(result), fileId: id },])
    return URL.createObjectURL(result)
  }
  const fetchData = async () => {
    try {
      const result = await fetchSurvey()
      setSurvey({
        title: result.title,
        description: result.description,
      })
      setDoneQuestions(result.questions)
      const arrayOfFileids = result.questions
        .filter((question) => question.type === 'IMAGE')
        .map((question) =>
          question.answerOptions.map((option) => option.fileId)
        )
        .flat(1)

      const arrayOfImages = await Promise.all(
        arrayOfFileids.map(async (id) => await fetchSurveyAnswerImage(id)) //is empty skip
      )

      setSentImages({ files: arrayOfImages, filesId: arrayOfFileids })
    } catch {
      //notify('Nie udało się pobrać ankiety', 'error')
    }
  }
  useEffect(() => {
    fetchData()
  }, [])
  const handleSubmit = form.handleSubmit((values) => {
    const normalisedValues = Object.entries(values).map((e) => ({
      questionId: parseInt(e[0]),
      answerOptionIds: Array.isArray(e[1])
        ? e[1].map((v) => parseInt(v))
        : Number.isInteger(parseInt(e[1]))
        ? [parseInt(e[1])]
        : null,
      shortAnswer:
        doneQuestions.find((question) => question.id === parseInt(e[0]))
          ?.type === 'SHORT'
          ? e[1]
          : null,
      longAnswer:
        doneQuestions.find((question) => question.id === parseInt(e[0]))
          ?.type === 'LONG'
          ? e[1]
          : null,
    }))
    mutate(normalisedValues)
    setApproveSendModal(false)
  })
  return (
    <>
      <SurveyWrapper>
        <TitleWrapper>
          <Title>Tytuł ankiety</Title>
          <H2>{survey.title}</H2>
          <Title>Opis</Title>
          <Description>{survey.description}</Description>
        </TitleWrapper>
        <QuestionsWrapper onSubmit={handleSubmit}>
          {doneQuestions !== [] &&
            doneQuestions.map((question, index) => (
              <QuestionWrapper key={index}>
                <Title>Pytanie {index + 1} </Title>
                <span>
                  {
                    questionType.find((types) => types.value === question.type)
                      ?.description
                  }
                </span>
                <Question>{question.question}</Question>
                {(question.type === 'SHORT' || question.type === 'LONG') && (
                  <>
                    <label>
                      Odpowiedź
                      <Input
                        maxLength={
                          (question.type === 'LONG' && 500) ||
                          (question.type === 'SHORT' && 100)
                        }
                        ref={form.register}
                        name={question.id}
                        placeholder="Wpisz odpowiedź"
                      ></Input>
                    </label>
                  </>
                )}
                <AnswersWrapper isImage={question.type === 'IMAGE'}>
                  {question.answerOptions?.map((answer, index) => (
                    <Fragment key={answer.id}>
                      {question.type === 'MULTIPLE' && (
                        <div>
                          <input
                            value={answer.id}
                            type="checkbox"
                            ref={form.register}
                            name={question.id}
                            key={index}
                          ></input>
                          <StyledSpan>{answer.answer}</StyledSpan>
                        </div>
                      )}
                      {(question.type === 'SINGLE' ||
                        question.type === 'LIKERT') && (
                        <div>
                          <input
                            ref={form.register}
                            name={question.id}
                            type="radio"
                            value={answer.id}
                          ></input>
                          <StyledSpan>{answer.answer}</StyledSpan>
                        </div>
                      )}
                      {question.type === 'IMAGE' && answer.fileId !== null && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginRight: '1em',
                          }}
                        >
                          <label>
                            <HiddenRadio
                              ref={form.register}
                              name={question.id}
                              type="radio"
                              value={answer.id}
                            ></HiddenRadio>
                            {sentImages.files.length !== 0 ? (
                              <Image
                                src={
                                  sentImages.files[
                                    sentImages?.filesId.indexOf(answer.fileId)
                                  ]
                                }
                              ></Image>
                            ) : (
                              <PlaceHolder />
                            )}
                          </label>
                          <span>{answer.answer}</span>
                        </div>
                      )}
                    </Fragment>
                    //EDYCJA WEWNATRZ, DAMY RADE
                  ))}
                </AnswersWrapper>
              </QuestionWrapper>
            ))}
          {!preview && (
            <StyledButton
              label={'Wyślij'}
              type="button"
              link={null}
              icon={null}
              onClick={() => setApproveSendModal(true)}
            />
          )}
          <Modal
            isOpen={approveSendModal}
            onClose={() => setApproveSendModal(false)}
            closer={true}
            shouldCloseOnOverlayClick
          >
            {isFilled ? (
              <P>
                Nie udzielono odpowiedzi na wszystkie pytania, czy wysłać
                wyniki?
              </P>
            ) : (
              <P>
                Udzielono odpowiedzi na wszystkie pytania, czy wysłać wyniki?
              </P>
            )}

            <ModalFooter>
              <Button
                icon={null}
                link={null}
                label="Anuluj"
                width="45%"
                color="grey"
                onClick={() => setApproveSendModal(false)}
                altTag=""
                srText=""
              />
              <Button
                icon={null}
                link={null}
                label="Wyślij"
                type="submit"
                onClick={handleSubmit}
                width="45%"
                altTag=""
                srText=""
              />
            </ModalFooter>
          </Modal>
        </QuestionsWrapper>
      </SurveyWrapper>
    </>
  )
}

export default Survey

const Image = styled.img`
  max-width: 200px;
  min-width: 200px;
  max-height: 112.5px;
  cursor: pointer;
  border: 2px solid #ddd};
  border-radius: 4px;
`
const QuestionsWrapper = styled.form`
  display: flex;
  flex-direction: column;
`
const QuestionWrapper = styled.div`
  margin: 0 5vw;
`
const SurveyWrapper = styled.div`
  margin: 10vh 10vw;
`

const TitleWrapper = styled.div``

const AnswersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${(props) => (props.isImage ? 'row' : 'column')};
`

const Input = styled.input`
  min-width: 97.5%;
  margin: 5px 0;
  border: ${(props) =>
    props.selected ? `2px solid ${colors.monza}` : '2px solid #ddd'};
  font-size: 14px;
  font-weight: 100;
  padding: 10px 15px;
  border-radius: 4px;
  height: 46px;
  box-sizing: border-box;
  &:disabled {
    background: #ebebe4;
  }
  &:focus {
    outline: 2px solid ${colors.monza};
    box-shadow: 0 0 0 1px ${colors.monza};
  }
`
const Title = styled.p`
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 21px;
  margin-bottom: 1em;
  text-transform: uppercase;
  margin-top: 2em;
`
const H2 = styled.h2`
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 23px;
`
const Description = styled.p`
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 19px;
`
const Question = styled.p`
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
`
const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  &:checked + img {
    border: 2px solid ${colors.monza};
  }
`
const StyledButton = styled(Button)`
  align-self: flex-end;
  width: 10%;
`

const StyledSpan = styled.span`
  margin-left: 0.5rem;
`
const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;

  &::before {
    position: absolute;
    left: 0;
    bottom: 6.5rem;
    content: '';
    border-bottom: 1px solid #eee;
    width: 100%;
  }
`
const P = styled.p`
  line-height: 1.3rem;
  margin-bottom: 4rem;
`
